import axios from 'axios';

// Get API URL from environment variable
// Automatically detects local vs production
const getApiUrl = () => {
  // Priority 1: If REACT_APP_API_URL is set (via .env or build-time), use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Priority 2: Check if we're running locally
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isLocalIP = /^(192\.168|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(hostname);

    // Check for Electron local server (port 4400)
    if (isLocalhost || isLocalIP) {
      const port = window.location.port;
      // If we are currently on port 4400 (Electron), use it for API too
      if (port === '4400') {
        return `http://${hostname}:4400/api`;
      }
      // Otherwise fallback to default local backend if in dev
      if (process.env.NODE_ENV === 'development') {
        return `http://${hostname}:5000/api`;
      }
    }

    // For any other hostname (production IP address, domain, etc.), use production backend
    return 'https://mandi-trade-backend.vercel.app/api';
  }

  // Priority 3: Check NODE_ENV for production (fallback)
  if (process.env.NODE_ENV === 'production') {
    return 'https://mandi-trade-backend.vercel.app/api';
  }

  // Priority 4: Default to production URL for safety
  return 'https://mandi-trade-backend.vercel.app/api';
};

// Create a new instance of Axios
const api = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000, // Increased timeout to 30 seconds for slow connections
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Log request only in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.error('[API Request Error]', error);
    }
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Log response only in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status
      });
    }
    return response;
  },
  (error) => {
    // Only log errors in development, hide sensitive info in production
    if (process.env.NODE_ENV === 'development') {
      if (error.response) {
        // Server responded with error status
        console.error('[API Error Response]', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
      } else if (error.request) {
        // Request made but no response
        console.error('[API Error No Response]', {
          url: error.config?.url,
          method: error.config?.method,
          message: error.message
        });
      } else {
        // Error in request setup
        console.error('[API Error Setup]', error.message);
      }
    }
    return Promise.reject(error);
  }
);

// Log API URL only in development (for debugging)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  console.log('API Base URL:', api.defaults.baseURL);
  console.log('Current Hostname:', window.location.hostname);
  console.log('NODE_ENV:', process.env.NODE_ENV);
}

export default api;
